import styled from "styled-components";
import { APP_NAME } from "../../helpers/constants";
import { device } from "../../lib/breakpoints";
import { Link } from "react-router-dom";
import Button from "../../components/Button";

const GoldCheck = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginRight: 8, verticalAlign: "middle" }}
  >
    <circle cx="10" cy="10" r="10" fill="#CDAF63" />
    <path
      d="M6 10.5L9 13.5L14 8.5"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function SignUpCta() {
  return (
    <Container className="section-fade-in">
      <CTATitle>Ready to Transform Your Consultations?</CTATitle>
      <CTASubtitle>
        Join leading clinics using <b>{APP_NAME}</b> to convert more leads and
        reduce no-shows with AI.
      </CTASubtitle>

      <CTAButtonsContainer>
        <ContactButton as={Link} to="/contact">
          Have Questions? Contact Us
        </ContactButton>
      </CTAButtonsContainer>

      <CTAFeatures>
        <FeatureItem>
          <GoldCheck />
          Reduce No-Shows
        </FeatureItem>
        <FeatureItem>
          <GoldCheck />
          Increase Conversions
        </FeatureItem>
        <FeatureItem>
          <GoldCheck />
          Setup in Minutes, ROI in Days
        </FeatureItem>
      </CTAFeatures>

      <ImplementationInfo>
        <ImplementationTitle>Implementation Made Simple</ImplementationTitle>
        <ImplementationDetails>
          <ImplementationItem>
            <GoldCheck />
            Instructions sent after payment
          </ImplementationItem>
          <ImplementationItem>
            <GoldCheck />
            Setup in 10 minutes or less
          </ImplementationItem>
          <ImplementationItem>
            <GoldCheck />
            Free help within 24 business hours
          </ImplementationItem>
        </ImplementationDetails>
      </ImplementationInfo>
    </Container>
  );
}

const Container = styled.div`
  text-align: center;
  padding: 4.5rem 2.5rem 4rem 2.5rem;
  background: var(--gradient-gold-luxury);
  border-radius: 24px;
  color: var(--color-text);
  box-shadow: 0 8px 30px var(--shadow-gold);
  border: 1px solid rgba(205, 175, 99, 0.15);
  position: relative;
  overflow: hidden;
  max-width: 900px;
  margin: 0 auto;

  @media ${device.mobile} {
    padding: 2.5rem 1rem 2rem 1rem;
  }
`;

const CTATitle = styled.h3`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
  font-family: var(--font-primary);
  letter-spacing: -0.02em;
  color: var(--color-accent);

  @media ${device.mobile} {
    font-size: 2rem;
  }
`;

const CTASubtitle = styled.p`
  font-size: 1.25rem;
  max-width: 600px;
  margin: 0 auto 2.5rem auto;
  line-height: 1.6;
  font-family: var(--font-secondary);
  letter-spacing: -0.005em;
  font-weight: 400;
  color: var(--color-accent);
  opacity: 0.95;

  @media ${device.mobile} {
    font-size: 1.1rem;
  }
`;

const CTAButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2.5rem;
`;

const ContactButton = styled(Button)`
  background: #fff;
  color: black;
  border: none;
  border-radius: 50px;
  font-size: 1.125rem;
  font-weight: 700;
  padding: 1.1rem 2.5rem;
  box-shadow: 0 4px 16px rgba(205, 175, 99, 0.13);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 0 auto;
  display: block;
  letter-spacing: -0.01em;
  font-family: var(--font-secondary);

  &:hover {
    color: var(--color-accent);
    box-shadow: 0 8px 24px var(--shadow-gold);
    transform: scale(1.04);
  }
`;

const CTAFeatures = styled.div`
  display: flex;
  justify-content: center;
  gap: 2.5rem;
  margin-bottom: 2.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;

  @media ${device.mobile} {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
  }
`;

const FeatureItem = styled.div`
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--color-accent);
  display: flex;
  align-items: center;
  font-family: var(--font-secondary);
  background: rgba(255, 255, 255, 0.18);
  border-radius: 32px;
  padding: 0.5rem 1.25rem;
  box-shadow: 0 2px 8px rgba(205, 175, 99, 0.07);
`;

const ImplementationInfo = styled.div`
  margin-top: 2.5rem;
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  background: rgba(255, 255, 255, 0.55);
  border-radius: 18px;
  text-align: center;
  border: 1.5px solid var(--color-primary-light);
  box-shadow: 0 2px 12px rgba(205, 175, 99, 0.07);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ImplementationTitle = styled.h4`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
  text-align: center;
  font-family: var(--font-primary);
  color: var(--color-accent);
`;

const ImplementationDetails = styled.ul`
  list-style-type: none;
  padding-left: 0;
  text-align: center;
`;

const ImplementationItem = styled.li`
  font-size: 1.05rem;
  margin-bottom: 0.75rem;
  text-align: center;
  font-family: var(--font-secondary);
  color: var(--color-accent);
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
`;
